import type { RouterOutputs } from "@gately/api";
import type { ColumnDef } from "@gately/ui/data-table";
import { useEffect, useState } from "react";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { z } from "zod/v4";

import { DataTable } from "@gately/ui/data-table";
import { StatusBadge } from "@gately/ui/status-badge";
import { Switch } from "@gately/ui/switch";

import { VisitorSheet } from "~/components/visitors/visitor-sheet";
import { fmtDate } from "~/lib/dashboard-format";
import { orpc, orpcClient } from "~/lib/orpc";
import { resolvePermissions } from "~/lib/use-permissions";

/** The two tag filters are booleans, so they are `?vip=true` / `?blocked=true`
 *  rather than faceted params: a filtered répertoire stays shareable and
 *  survives a reload, same contract as /activite. */
const searchSchema = z.object({
  q: z.string().min(1).optional().catch(undefined),
  vip: z.boolean().optional().catch(undefined),
  blocked: z.boolean().optional().catch(undefined),
});

type RepertoireSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_dashboard/repertoire")({
  validateSearch: (s) => searchSchema.parse(s),
  // Management surface: the directory carries phone/email and the tags that
  // change what the gardien is told, so it is `canManage` only — the same gate
  // /activite and /invitations sit behind.
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(
      orpc.me.get.queryOptions(),
    );
    if (!resolvePermissions(me).canManage) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: "/" });
    }
  },
  component: RepertoirePage,
});

const PAGE_SIZE = 50;

type VisitorRow = RouterOutputs["visitor"]["list"]["items"][number];

/* -------------------------------------------------------------------- page */

function filterInput(search: RepertoireSearch) {
  return {
    ...(search.q ? { q: search.q } : {}),
    ...(search.vip ? { vip: true } : {}),
    ...(search.blocked ? { blocked: true } : {}),
  };
}

function RepertoirePage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [openId, setOpenId] = useState<string | null>(null);

  // Search box → ?q= after 300 ms. The list is keyset-paginated, so `q` has to
  // reach the server; the grid never filters the loaded window itself.
  const [q, setQ] = useState(search.q ?? "");
  useEffect(() => {
    const t = setTimeout(() => {
      const next = q.trim() || undefined;
      if (next !== search.q) {
        void navigate({
          search: (prev) => ({ ...prev, q: next }),
          replace: true,
        });
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q, search.q, navigate]);

  function setFilter(patch: Partial<RepertoireSearch>) {
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  }

  const filters = filterInput(search);
  // An empty grid means two different things: nothing matched what was asked
  // for, or the directory itself is still empty. Only the second one should be
  // told how the directory fills up.
  const isFiltered = Object.keys(filters).length > 0;

  const query = useInfiniteQuery({
    queryKey: [...orpc.visitor.list.key(), filters],
    queryFn: ({ pageParam }) =>
      orpcClient.visitor.list({
        ...filters,
        limit: PAGE_SIZE,
        cursor: pageParam ?? undefined,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    placeholderData: keepPreviousData,
  });

  const rows = query.data?.pages.flatMap((p) => p.items) ?? [];

  const columns: ColumnDef<VisitorRow>[] = [
    {
      accessorKey: "name",
      size: 220,
      minSize: 160,
      enableHiding: false,
      meta: { label: "Nom" },
      header: "Nom",
      // The name IS the affordance: DataTable has no row-click hook, and a
      // whole clickable row would swallow text selection in the other cells.
      cell: ({ row }) => (
        <button
          type="button"
          onClick={() => setOpenId(row.original.id)}
          className="text-left font-medium hover:underline"
        >
          {row.original.name}
        </button>
      ),
    },
    {
      id: "contact",
      size: 220,
      minSize: 150,
      meta: { label: "Contact" },
      header: "Contact",
      cell: ({ row }) => (
        <span className="text-muted-foreground">
          {row.original.phone ?? row.original.email ?? "—"}
        </span>
      ),
    },
    {
      accessorKey: "company",
      size: 160,
      minSize: 120,
      meta: { label: "Société" },
      header: "Société",
      cell: ({ getValue }) => getValue<string | null>() ?? "—",
    },
    {
      id: "tags",
      size: 200,
      minSize: 140,
      meta: { label: "Étiquettes" },
      header: "Étiquettes",
      cell: ({ row }) => (
        <span className="flex gap-1.5">
          {row.original.vip ? (
            <StatusBadge tone="warning">VIP</StatusBadge>
          ) : null}
          {row.original.blocked ? (
            <StatusBadge tone="danger">Liste noire</StatusBadge>
          ) : null}
          {!row.original.vip && !row.original.blocked ? (
            <span className="text-muted-foreground">—</span>
          ) : null}
        </span>
      ),
    },
    {
      accessorKey: "visitCount",
      size: 100,
      minSize: 80,
      meta: { label: "Visites" },
      header: "Visites",
    },
    {
      accessorKey: "lastSeenAt",
      size: 150,
      minSize: 120,
      meta: { label: "Dernière visite" },
      header: "Dernière visite",
      cell: ({ getValue }) => (
        <span className="text-muted-foreground whitespace-nowrap">
          {fmtDate(getValue<string | Date | null>())}
        </span>
      ),
    },
    {
      accessorKey: "consentAt",
      size: 140,
      minSize: 110,
      meta: { label: "Consentement" },
      header: "Consentement",
      cell: ({ getValue }) => (
        <span className="text-muted-foreground">
          {getValue<string | Date | null>() ? "oui" : "—"}
        </span>
      ),
    },
  ];

  return (
    <div className="px-7 py-6">
      <div className="mb-5">
        <h1 className="text-foreground text-2xl font-semibold tracking-[-0.02em]">
          Visiteurs
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Toutes les personnes déjà venues, les plus récentes d&apos;abord.
        </p>
      </div>

      <DataTable
        columns={columns}
        data={rows}
        isLoading={query.isPending}
        initialPinning={{ left: ["name"] }}
        showColumnToggle
        search={{
          value: q,
          onChange: setQ,
          placeholder: "Rechercher nom, téléphone, email, société…",
        }}
        toolbar={
          <>
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={Boolean(search.vip)}
                onCheckedChange={(on) => setFilter({ vip: on || undefined })}
              />
              VIP
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={Boolean(search.blocked)}
                onCheckedChange={(on) =>
                  setFilter({ blocked: on || undefined })
                }
              />
              Liste noire
            </label>
          </>
        }
        empty={
          isFiltered
            ? {
                title: "Aucun visiteur ne correspond",
                description: "Modifiez la recherche ou les filtres.",
              }
            : {
                title: "Aucun visiteur au répertoire.",
                // A phone is what links an arrivée imprévue to a visitor row
                // (`resolveVisitor` runs only when one was typed), so the old
                // copy promised something an anonymous walk-in never delivers.
                description:
                  "Le répertoire se remplit tout seul : chaque pass émis y ajoute la personne, ainsi que chaque arrivée imprévue saisie avec un numéro de téléphone.",
              }
        }
        manual={{
          hasNextPage: query.hasNextPage,
          isFetchingNextPage: query.isFetchingNextPage,
          onLoadMore: () => void query.fetchNextPage(),
        }}
      />

      <VisitorSheet visitorId={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}
